import { APP_HEADER_HEIGHT } from 'features/navigation/public/layout';

export interface ReportLayoutTokens {
    headerHeight: number;
    leftDrawer: {
        expandedWidth: number;
        collapsedWidth: number;
    };
    dataChatDrawer: {
        defaultWidth: number;
        minWidth: number;
        maxWidth: number;
        collapsedWidth: number;
    };
    rightDrawer: {
        defaultWidth: number;
        minWidth: number;
        maxWidth: number;
        collapsedWidth: number;
    };
    filtersDrawer: {
        defaultWidth: number;
        minWidth: number;
        maxWidth: number;
        collapsedWidth: number;
    };
    bars: {
        actionHeight: number;
        dataChatHeight: number;
    };
    viewport: {
        maxContentWidth: number;
    };
}

export const REPORT_LAYOUT_TOKENS: ReportLayoutTokens = {
    headerHeight: APP_HEADER_HEIGHT,
    leftDrawer: {
        expandedWidth: 240,
        collapsedWidth: 50,
    },
    dataChatDrawer: {
        defaultWidth: 480,
        minWidth: 360,
        maxWidth: 720,
        collapsedWidth: 48,
    },
    rightDrawer: {
        defaultWidth: 315,
        minWidth: 315,
        maxWidth: 900,
        collapsedWidth: 50,
    },
    filtersDrawer: {
        defaultWidth: 340,
        minWidth: 280,
        maxWidth: 520,
        collapsedWidth: 48,
    },
    bars: {
        actionHeight: 44,
        dataChatHeight: 0,
    },
    viewport: {
        maxContentWidth: 2000,
    },
};

export type WorkspacePanel = 'chat' | 'filters' | 'inspector';
/** The report canvas keeps at least this width while side panels are open. */
export const MIN_CANVAS_WIDTH = 400;
export interface WorkspacePanelState { open: boolean; width: number; collapsedWidth: number }

/**
 * The panels to show collapsed so the canvas keeps ``MIN_CANVAS_WIDTH`` in a window of ``windowWidth``: the open
 * panels the reader opened longest ago first (``order``: least recent first), never the most recent one. Only
 * the layout changes; each panel's saved preference stays as it is.
 */
export function fitWorkspacePanels(windowWidth: number, leftWidth: number,
    panels: Partial<Record<WorkspacePanel, WorkspacePanelState>>, order: WorkspacePanel[],
    minCanvas: number = MIN_CANVAS_WIDTH): Set<WorkspacePanel> {
    const collapsed = new Set<WorkspacePanel>();
    const used = () => leftWidth + (Object.entries(panels) as [WorkspacePanel, WorkspacePanelState][])
        .reduce((sum, [id, panel]) => sum + (panel.open && !collapsed.has(id) ? panel.width : panel.collapsedWidth), 0);
    const open = order.filter(id => panels[id]?.open);
    for (const id of open.slice(0, -1)) {
        if (windowWidth - used() >= minCanvas) break;
        collapsed.add(id);
    }
    return collapsed;
}

export interface ReportLayoutStateSnapshot {
    preview?: boolean;
    isLeftCollapsed: boolean;
    isRightCollapsed: boolean;
    rightDrawerWidth: number;
    /** The app panel's width beside the report (0 when hidden); it replaces the pages drawer in every mode. */
    leftPanelWidth?: number;
    /** Presentation (preview) shows the report alone: no app header. */
    hideHeader?: boolean;
    isDataChatDrawerVisible?: boolean;
    isDataChatDrawerCollapsed?: boolean;
    dataChatDrawerWidth?: number;
    isFiltersDrawerVisible?: boolean;
    isFiltersDrawerCollapsed?: boolean;
    filtersDrawerWidth?: number;
}

export interface ReportLayoutMetrics {
    headerHeight: number;
    dataChatHeight: number;
    leftDrawerWidth: number;
    dataChatDrawerWidth: number;
    filtersDrawerWidth: number;
    leftContentOffset: number;
    rightDrawerWidth: number;
    isLeftCollapsed: boolean;
    isRightCollapsed: boolean;
    contentWidthStyle: string;
    viewportBottomOffset: number;
    actionBarOffsets: {
        left: number;
        right: number;
    };
    snackbarOffsets: {
        left: number;
        right: number;
    };
}

export const computeReportLayoutMetrics = (
    state: ReportLayoutStateSnapshot,
    tokens: ReportLayoutTokens = REPORT_LAYOUT_TOKENS,
): ReportLayoutMetrics => {
    const leftDrawerWidth = state.leftPanelWidth !== undefined ? state.leftPanelWidth : state.preview ? 0 : state.isLeftCollapsed
        ? tokens.leftDrawer.collapsedWidth
        : tokens.leftDrawer.expandedWidth;

    const effectiveRightDrawerWidth = state.preview ? 0 : state.isRightCollapsed
        ? tokens.rightDrawer.collapsedWidth
        : state.rightDrawerWidth;

    const isDataChatDrawerVisible = !state.preview && (state.isDataChatDrawerVisible ?? false);
    const resolvedDataChatWidth = typeof state.dataChatDrawerWidth === 'number' && state.dataChatDrawerWidth > 0
        ? state.dataChatDrawerWidth
        : tokens.dataChatDrawer.defaultWidth;
    const clampedDataChatWidth = Math.min(
        tokens.dataChatDrawer.maxWidth,
        Math.max(tokens.dataChatDrawer.minWidth, resolvedDataChatWidth),
    );
    const dataChatDrawerWidth = isDataChatDrawerVisible
        ? (state.isDataChatDrawerCollapsed ? tokens.dataChatDrawer.collapsedWidth : clampedDataChatWidth)
        : 0;
    // The filter pane shows in preview too: readers change their filters there (decision 32).
    const isFiltersDrawerVisible = state.isFiltersDrawerVisible ?? false;
    const resolvedFiltersWidth = typeof state.filtersDrawerWidth === 'number' && state.filtersDrawerWidth > 0
        ? state.filtersDrawerWidth
        : tokens.filtersDrawer.defaultWidth;
    const clampedFiltersWidth = Math.min(
        tokens.filtersDrawer.maxWidth,
        Math.max(tokens.filtersDrawer.minWidth, resolvedFiltersWidth),
    );
    const filtersDrawerWidth = isFiltersDrawerVisible
        ? (state.isFiltersDrawerCollapsed ? tokens.filtersDrawer.collapsedWidth : clampedFiltersWidth)
        : 0;
    const totalRightDrawerWidth = effectiveRightDrawerWidth + filtersDrawerWidth;
    const leftContentOffset = leftDrawerWidth + dataChatDrawerWidth;

    return {
        headerHeight: state.hideHeader ? 0 : tokens.headerHeight,
        dataChatHeight: tokens.bars.dataChatHeight,
        leftDrawerWidth,
        dataChatDrawerWidth,
        filtersDrawerWidth,
        leftContentOffset,
        rightDrawerWidth: totalRightDrawerWidth,
        isLeftCollapsed: state.isLeftCollapsed,
        isRightCollapsed: state.isRightCollapsed,
        contentWidthStyle: `calc(100% - ${totalRightDrawerWidth + dataChatDrawerWidth}px)`,
        viewportBottomOffset: tokens.bars.dataChatHeight,
        actionBarOffsets: {
            left: leftContentOffset,
            right: totalRightDrawerWidth,
        },
        snackbarOffsets: {
            left: leftContentOffset,
            right: totalRightDrawerWidth,
        },
    };
};

